import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import ConsoleHeader from './ConsoleHeader';
import ConsoleToolbar from './ConsoleToolbar';
import CompanyGroup, { CompanyGroupData } from './CompanyGroup';
import ActionTrailModal from './ActionTrailModal';
import {
  AI_RECRUITER_CONSOLE,
  AI_RECRUITER_NEEDS_HUMAN,
  AI_RECRUITER_COMPLETION,
} from './gql';
import { pruneToLiveIds } from './selection.util';
import { isFitBucket } from './fit-bucket.util';
import './ai-recruiter-console.scss';

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

type ActiveTab = 'pipeline' | 'needs-human' | 'completion';

type ModalState =
  | { type: 'trail'; pipelineEntryId: string; candidateName: string | null | undefined; candidateTitle: string | null | undefined }
  | null;

// ---------------------------------------------------------------------------
// Status enum → filter value map
// The toolbar shows human-readable labels; the server expects enum keys.
// ---------------------------------------------------------------------------

const STATUS_LABEL_TO_ENUM: Record<string, string> = {
  'New': 'NEW',
  'Send outreach': 'SEND_OUTREACH',
  'In queue': 'IN_QUEUE',
  'Outreach sent': 'OUTREACH_SENT',
  'Awaiting response': 'AWAITING_RESPONSE',
  'Scheduled': 'SCHEDULED',
  'Interviewed': 'INTERVIEWED',
  'Completed': 'COMPLETED',
  'Needs human outreach': 'NEEDS_HUMAN_OUTREACH',
  'Assigned to recruiter': 'ASSIGNED_TO_RECRUITER',
  'Declined by admin': 'DECLINED_BY_ADMIN',
};

// Status-filter options per tab, in funnel/lifecycle order (matches the backend
// PIPELINE_STATUSES / COMPLETION_STATUSES sets). Pipeline = the active journey;
// Completion = terminal outcomes.
const TAB_STATUS_OPTIONS: Record<ActiveTab, readonly string[]> = {
  pipeline: [
    'New',
    'Send outreach',
    'In queue',
    'Outreach sent',
    'Awaiting response',
    'Scheduled',
    'Interviewed',
  ],
  'needs-human': ['Needs human outreach'],
  completion: ['Completed', 'Assigned to recruiter', 'Declined by admin'],
};

// ---------------------------------------------------------------------------
// View-state persistence — the active tab + filters live in the URL query
// string so they survive a refresh and are shareable (matches how other pages
// in the app persist view state).
// Shape: ?tab=<tab>&q=<search>&status=<labels>&fit=<buckets>&sort=fit
// ---------------------------------------------------------------------------

interface PersistedViewState {
  tab: ActiveTab;
  search: string;
  statusFilters: string[];
  fitBuckets: string[];
  sortByFit: boolean;
}

const isActiveTab = (v: string | null): v is ActiveTab =>
  v === 'pipeline' || v === 'needs-human' || v === 'completion';

// Parse the query string into a normalized view state. Status labels are kept
// only when valid for the (persisted) tab, since each tab has its own vocabulary.
// Fit buckets are tab-agnostic and validated by isFitBucket regardless of tab.
const parseViewState = (searchStr: string): PersistedViewState => {
  const params = new URLSearchParams(searchStr);
  const tabParam = params.get('tab');
  const tab: ActiveTab = isActiveTab(tabParam) ? tabParam : 'pipeline';
  const search = params.get('q') ?? '';
  const statusParam = params.get('status');
  const statusFilters = statusParam
    ? statusParam.split(',').filter((s) => TAB_STATUS_OPTIONS[tab].includes(s))
    : [];
  const fitParam = params.get('fit');
  const fitBuckets = fitParam ? fitParam.split(',').filter(isFitBucket) : [];
  const sortByFit = params.get('sort') === 'fit';
  return { tab, search, statusFilters, fitBuckets, sortByFit };
};

// Serialize view state back into a query string (leading '?'), omitting empties.
const serializeViewState = (view: PersistedViewState): string => {
  const params = new URLSearchParams();
  params.set('tab', view.tab);
  if (view.search) params.set('q', view.search);
  if (view.statusFilters.length > 0) params.set('status', view.statusFilters.join(','));
  if (view.fitBuckets.length > 0) params.set('fit', view.fitBuckets.join(','));
  if (view.sortByFit) params.set('sort', 'fit');
  return `?${params.toString()}`;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const AiRecruiterConsole: FC = () => {
  const history = useHistory();
  const location = useLocation();

  // ---- Filter state ----
  // Seed the tab + filters from the URL query string once on mount so a refresh
  // (or a shared link) restores the view. The URL is kept in sync below.
  const [initialView] = useState<PersistedViewState>(() =>
    parseViewState(location.search),
  );
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialView.tab);
  const [search, setSearch] = useState(initialView.search);
  const [statusFilters, setStatusFilters] = useState<string[]>(initialView.statusFilters);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [fitBuckets, setFitBuckets] = useState<string[]>(initialView.fitBuckets);
  const [sortByFit, setSortByFit] = useState<boolean>(initialView.sortByFit);
  const [fitMenuOpen, setFitMenuOpen] = useState(false);

  // ---- Expand/collapse state ----
  // Companies default expanded (true), tracked by companyId
  const [expandedCompanies, setExpandedCompanies] = useState<Record<string, boolean>>({});
  // Job orders default collapsed (false), tracked by jobOrderId
  const [expandedJobOrders, setExpandedJobOrders] = useState<Record<string, boolean>>({});

  // ---- Intake state ----
  // Which job order has the intake dropzone open (at most one at a time)
  const [intakeJobOrderId, setIntakeJobOrderId] = useState<string | null>(null);

  // ---- Selection state ----
  // Selection is scoped to a single job order.
  const [selectionJoId, setSelectionJoId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Mirror of selectedIds so the [activeData]-keyed prune effect can read the
  // current value without a stale closure (and without a side-effecting updater).
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  // ---- Modal state ----
  const [modal, setModal] = useState<ModalState>(null);

  // ---- Optimistic-hide state (Fix C) ----
  // Ids added here are filtered out of the rendered rows immediately after a
  // bulk action fires, covering the cache-and-network window before the refetch
  // resolves and drops the rows from Apollo's cache.
  const [pendingHiddenIds, setPendingHiddenIds] = useState<Set<string>>(new Set());

  // ---- Build server filter variables ----
  const serverStatuses = statusFilters.map((label) => STATUS_LABEL_TO_ENUM[label]).filter(Boolean);

  const queryVariables = {
    filter: {
      search: search || undefined,
      statuses: serverStatuses.length > 0 ? serverStatuses : undefined,
      fitBuckets: fitBuckets.length > 0 ? fitBuckets : undefined,
      sortByFit: sortByFit || undefined,
    },
  };

  // isFiltering drives the results-summary bar and the auto-expand-on-filter
  // behavior. sortByFit is intentionally excluded: sort rearranges rows but hides
  // nothing, so the summary bar and auto-expand must not activate on sort alone.
  const isFiltering = search.trim().length > 0 || statusFilters.length > 0 || fitBuckets.length > 0;

  // ---- Pipeline tab query ----
  const {
    data: pipelineData,
    loading: pipelineLoading,
    error: pipelineError,
    refetch: refetchPipeline,
  } = useQuery(AI_RECRUITER_CONSOLE, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    skip: activeTab !== 'pipeline',
  });

  // ---- Needs-human tab query ----
  const {
    data: needsHumanData,
    loading: needsHumanLoading,
    error: needsHumanError,
    refetch: refetchNeedsHuman,
  } = useQuery(AI_RECRUITER_NEEDS_HUMAN, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    skip: activeTab !== 'needs-human',
  });

  // ---- Completion tab query ----
  const {
    data: completionData,
    loading: completionLoading,
    error: completionError,
    refetch: refetchCompletion,
  } = useQuery(AI_RECRUITER_COMPLETION, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    skip: activeTab !== 'completion',
  });

  const activeData =
    activeTab === 'pipeline'
      ? pipelineData?.aiRecruiterConsole
      : activeTab === 'needs-human'
        ? needsHumanData?.aiRecruiterNeedsHuman
        : completionData?.aiRecruiterCompletion;
  const activeLoading =
    activeTab === 'pipeline'
      ? pipelineLoading
      : activeTab === 'needs-human'
        ? needsHumanLoading
        : completionLoading;
  const activeError =
    activeTab === 'pipeline'
      ? pipelineError
      : activeTab === 'needs-human'
        ? needsHumanError
        : completionError;

  const companies: CompanyGroupData[] = activeData?.companies ?? [];
  const totalApplicants: number = activeData?.totalApplicants ?? 0;
  const totalCompanies = companies.length;

  // ---- Expand/collapse handlers ----
  const handleToggleCompany = useCallback((companyId: string) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [companyId]: prev[companyId] === false ? true : !prev[companyId],
    }));
  }, []);

  const handleToggleJobOrder = useCallback((jobOrderId: string) => {
    setExpandedJobOrders((prev) => ({
      ...prev,
      [jobOrderId]: !prev[jobOrderId],
    }));
  }, []);

  // ---- Intake handlers ----
  const handleToggleIntake = useCallback((jobOrderId: string) => {
    setIntakeJobOrderId((prev) => (prev === jobOrderId ? null : jobOrderId));
  }, []);

  const handleIntakeUploaded = useCallback(
    (_jobOrderId: string, _batchId: string) => {
      // IntakeDropzone polls AI_INTAKE_BATCH_STATUS; when terminal, trigger refetch.
      // For now the dropzone manages its own polling and calls onUploaded once to
      // surface the batchId; the dropzone itself will refetch when done.
      if (activeTab === 'pipeline') {
        refetchPipeline();
      } else if (activeTab === 'needs-human') {
        refetchNeedsHuman();
      } else {
        refetchCompletion();
      }
    },
    [activeTab, refetchPipeline, refetchNeedsHuman, refetchCompletion]
  );

  // ---- Selection handlers ----
  const handleToggleSelect = useCallback(
    (jobOrderId: string, pipelineEntryId: string) => {
      if (selectionJoId !== null && selectionJoId !== jobOrderId) {
        // Selecting in a different JO — replace the entire selection
        setSelectionJoId(jobOrderId);
        setSelectedIds([pipelineEntryId]);
        return;
      }
      setSelectionJoId(jobOrderId);
      setSelectedIds((prev) =>
        prev.includes(pipelineEntryId)
          ? prev.filter((id) => id !== pipelineEntryId)
          : [...prev, pipelineEntryId]
      );
    },
    [selectionJoId]
  );

  const handleSelectAll = useCallback(
    (jobOrderId: string, pipelineEntryIds: string[], checked: boolean) => {
      if (selectionJoId !== null && selectionJoId !== jobOrderId) {
        // Selecting all in a different JO — replace selection
        if (checked) {
          setSelectionJoId(jobOrderId);
          setSelectedIds(pipelineEntryIds);
        }
        return;
      }
      setSelectionJoId(jobOrderId);
      setSelectedIds(checked ? pipelineEntryIds : []);
    },
    [selectionJoId]
  );

  const handleClearSelection = useCallback(() => {
    setSelectionJoId(null);
    setSelectedIds([]);
  }, []);

  const handleActionDone = useCallback(async (actedIds: string[]) => {
    // Fix C: optimistically hide the acted rows immediately so they disappear
    // from the rendered list before the cache-and-network refetch resolves.
    if (actedIds.length > 0) {
      setPendingHiddenIds((prev) => {
        const next = new Set(prev);
        actedIds.forEach((id) => next.add(id));
        return next;
      });
    }
    handleClearSelection();
    try {
      if (activeTab === 'pipeline') {
        await refetchPipeline();
      } else if (activeTab === 'needs-human') {
        await refetchNeedsHuman();
      } else {
        await refetchCompletion();
      }
    } finally {
      // Cleanup: always un-hide the acted ids, even if the refetch rejected.
      // Otherwise a network blip would leave rows that legitimately stay in the
      // active tab (e.g. NEW -> SEND_OUTREACH is still a pipeline row) hidden
      // until remount. The next refetch/prune reconciles the true row set.
      if (actedIds.length > 0) {
        setPendingHiddenIds((prev) => {
          const next = new Set(prev);
          actedIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    }
  }, [activeTab, handleClearSelection, refetchPipeline, refetchNeedsHuman, refetchCompletion]);

  // ---- Modal handlers ----
  const handleOpenTrail = useCallback(
    (pipelineEntryId: string, candidateName: string | null | undefined, candidateTitle: string | null | undefined) => {
      setModal({ type: 'trail', pipelineEntryId, candidateName, candidateTitle });
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setModal(null);
  }, []);

  // ---- Filter clear ----
  // Clears search, status, and fit filters. Also resets sortByFit for a clean
  // slate. The sort is included here (alongside fit) because a "Clear" action
  // should return the view to its default state.
  const handleClearFilters = useCallback(() => {
    setSearch('');
    setStatusFilters([]);
    setFitBuckets([]);
    setSortByFit(false);
  }, []);

  // ---- Reschema callbacks ----
  const handleRescored = useCallback(() => {
    if (activeTab === 'pipeline') {
      refetchPipeline();
    } else if (activeTab === 'needs-human') {
      refetchNeedsHuman();
    } else {
      refetchCompletion();
    }
  }, [activeTab, refetchPipeline, refetchNeedsHuman, refetchCompletion]);

  // ---- Error toast ----
  useEffect(() => {
    if (activeError) {
      toast.error(`Failed to load console: ${activeError.message}`);
    }
  }, [activeError]);

  // ---- Fix A: prune selectedIds against the live row set after each refetch ----
  // After the cache-and-network refetch resolves, any id that was declined (or
  // otherwise removed) is no longer present in activeData. Pruning here keeps
  // the bar-count and Undo payload honest and covers tab-switch edge cases.
  useEffect(() => {
    if (!activeData) return;
    const liveIds = new Set<string>();
    (activeData.companies ?? []).forEach((company: CompanyGroupData) => {
      company.jobOrders.forEach((jo) => {
        jo.applicants.forEach((a) => {
          liveIds.add(a.pipelineEntryId);
        });
      });
    });
    const pruned = pruneToLiveIds(selectedIdsRef.current, liveIds);
    if (pruned === null) return;
    setSelectedIds(pruned);
    if (pruned.length === 0) {
      setSelectionJoId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeData]);

  // Switch tabs. Each tab has its own status vocabulary, so clear the status
  // filter on an explicit tab change. This is done in the click handler (not a
  // useEffect on [activeTab]) on purpose: an effect also runs on mount, and under
  // React 18 StrictMode it double-invokes, which would wipe a status filter
  // restored from the URL (?status=...) on refresh. A handler only fires on a
  // real user click, so a refreshed view keeps its persisted status.
  //
  // Fit buckets and sortByFit are deliberately NOT reset here — they are
  // tab-agnostic (decision #5) and must survive a tab switch.
  const handleTabChange = useCallback(
    (tab: ActiveTab) => {
      if (tab === activeTab) return;
      setActiveTab(tab);
      setStatusFilters([]);
    },
    [activeTab],
  );

  // Keep the URL query string in sync with the current tab + filters so the view
  // is remembered on refresh. Uses replace() to avoid pushing a history entry on
  // every keystroke/toggle. The equality guard prevents an update loop.
  useEffect(() => {
    const next = serializeViewState({ tab: activeTab, search, statusFilters, fitBuckets, sortByFit });
    if (next !== location.search) {
      history.replace({ search: next });
    }
  }, [activeTab, search, statusFilters, fitBuckets, sortByFit, location.search, history]);

  // ---- Compute which company groups are expanded ----
  // Default: expanded (true) unless explicitly collapsed
  const isCompanyExpanded = (companyId: string): boolean =>
    expandedCompanies[companyId] !== false;

  // When filtering, auto-expand companies and JOs that have matches
  const isJobOrderExpanded = (jobOrderId: string, matchCount: number): boolean => {
    if (isFiltering && matchCount > 0) return true;
    return !!expandedJobOrders[jobOrderId];
  };

  return (
    <div className="ai-console">
      <ConsoleHeader />

      {/* Tabs */}
      <div className="ai-console__tabs">
        <button
          type="button"
          className={`ai-console__tab ${activeTab === 'pipeline' ? 'ai-console__tab--active' : ''}`}
          onClick={() => handleTabChange('pipeline')}
        >
          Pipeline
        </button>
        <button
          type="button"
          className={`ai-console__tab ${activeTab === 'needs-human' ? 'ai-console__tab--active' : ''}`}
          onClick={() => handleTabChange('needs-human')}
        >
          Needs human
        </button>
        <button
          type="button"
          className={`ai-console__tab ${activeTab === 'completion' ? 'ai-console__tab--active' : ''}`}
          onClick={() => handleTabChange('completion')}
        >
          Completion
        </button>
      </div>

      <ConsoleToolbar
        search={search}
        onSearchChange={setSearch}
        statusFilters={statusFilters}
        onStatusFiltersChange={setStatusFilters}
        statusMenuOpen={statusMenuOpen}
        onStatusMenuToggle={() => setStatusMenuOpen((o) => !o)}
        statusOptions={TAB_STATUS_OPTIONS[activeTab]}
        statusDisabled={activeTab === 'needs-human'}
        fitBuckets={fitBuckets}
        onFitBucketsChange={setFitBuckets}
        fitMenuOpen={fitMenuOpen}
        onFitMenuToggle={() => setFitMenuOpen((o) => !o)}
        sortByFit={sortByFit}
        onSortByFitToggle={() => setSortByFit((v) => !v)}
        totalApplicants={totalApplicants}
        totalCompanies={totalCompanies}
        isFiltering={isFiltering}
        onClearFilters={handleClearFilters}
      />

      {/* Content area */}
      <div className="ai-console__content">
        {activeLoading && companies.length === 0 ? (
          <div className="ai-console__loading">
            <Spinner color="warning" />
          </div>
        ) : companies.length === 0 ? (
          <div className="ai-console__empty">
            {isFiltering
              ? 'No results match your filters.'
              : 'No AI-enabled job orders found. Enable AI Recruiter on a company and job order to get started.'}
          </div>
        ) : (
          companies.map((company) => (
            <CompanyGroup
              key={company.companyId}
              data={{
                ...company,
                jobOrders: company.jobOrders.map((jo) => ({
                  ...jo,
                  // Fix C: drop optimistically-hidden rows so they leave
                  // visibleIds in JobOrderBlock and cannot be re-selected or
                  // sent during the cache-and-network refetch window.
                  applicants: jo.applicants.filter(
                    (a) => !pendingHiddenIds.has(a.pipelineEntryId),
                  ),
                })),
              }}
              isExpanded={isCompanyExpanded(company.companyId)}
              onToggleExpand={handleToggleCompany}
              expandedJobOrders={Object.fromEntries(
                company.jobOrders.map((jo) => [
                  jo.jobOrderId,
                  isJobOrderExpanded(jo.jobOrderId, jo.matchCount),
                ])
              )}
              onToggleJobOrder={handleToggleJobOrder}
              intakeJobOrderId={intakeJobOrderId}
              onToggleIntake={handleToggleIntake}
              onIntakeUploaded={handleIntakeUploaded}
              selectionJoId={selectionJoId}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              onActionDone={handleActionDone}
              onOpenTrail={handleOpenTrail}
              onRescored={handleRescored}
              isFiltering={isFiltering}
            />
          ))
        )}
      </div>

      {/* Modals */}
      <ActionTrailModal
        isOpen={modal?.type === 'trail'}
        pipelineEntryId={modal?.type === 'trail' ? modal.pipelineEntryId : null}
        candidateName={modal?.type === 'trail' ? modal.candidateName : null}
        candidateTitle={modal?.type === 'trail' ? modal.candidateTitle : null}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default AiRecruiterConsole;
